import { z } from "zod";

import {
  isCarouselSocialPostType,
  isSocialPostTypeSupportedByPlatform,
  instagramSocialPostTypeSchema,
  socialPostPlatformSchema,
  socialPostImageBackgroundModeSchema,
  socialPostScenePlanV1Schema,
  socialPostTypeSchema,
} from "./social-post-scene.ts";
import {
  socialPostLayoutRecipeIdSchema,
  socialPostRecipeVisualToneSchema,
} from "./social-post-layout-recipes.ts";
import {
  socialPostLayoutTemplateIdSchema,
  socialPostLayoutTemplateNameSchema,
} from "./social-post-layout-templates.ts";
import {
  SOCIAL_POST_CAROUSEL_MIN_SLIDES,
  SOCIAL_POST_CAROUSEL_VISUAL_CONTINUITY_MODES,
  SOCIAL_POST_CAROUSEL_MAX_SLIDES,
  socialPostCarouselFormatKeySchema,
  socialPostCarouselPlanV1Schema,
  socialPostCarouselSlideRoleSchema,
} from "./social-post-carousel.ts";
import {
  assetCreationFlowSchema,
  socialPostImageQaFailurePolicySchema,
  socialPostImageRealizerProviderSchema,
} from "./runtime-flags.ts";
import { BRAND_REAL_PEOPLE_REFERENCE_POLICIES } from "./brand-reference-selection.ts";
import { audienceProfileSnapshotV1Schema } from "./audience-profile.ts";
import { effectiveAudienceContextV1Schema } from "./effective-audience-context.ts";
import { standaloneAssetBrandPresenceSchema } from "./standalone-assets.ts";
import {
  campaignBriefV1Schema,
  campaignContentSlotV1Schema,
  type CampaignBriefV1,
  type CampaignContentSlotV1,
} from "./campaign.ts";

export { socialPostImageQaFailurePolicySchema } from "./runtime-flags.ts";

export const GENERATION_TASK_KINDS = [
  "social_post",
  "social_post_copy",
  "social_publish",
  "social_post_image_regeneration",
  "assistant_image_generation",
] as const;
export type GenerationTaskKind = (typeof GENERATION_TASK_KINDS)[number];
export const generationTaskKindSchema = z.enum(GENERATION_TASK_KINDS);

export const GENERATION_TASK_STATUSES = ["queued", "processing", "done", "failed"] as const;
export type GenerationTaskStatus = (typeof GENERATION_TASK_STATUSES)[number];
export const generationTaskStatusSchema = z.enum(GENERATION_TASK_STATUSES);

export const GENERATED_ASSET_KINDS = ["image", "video"] as const;
export type GeneratedAssetKind = (typeof GENERATED_ASSET_KINDS)[number];
export const generatedAssetKindSchema = z.enum(GENERATED_ASSET_KINDS);

export const SOCIAL_POST_CAROUSEL_CONTINUITY_REFERENCE_ROLES = [
  "cast_reference_sheet",
  "setting_or_object_reference",
] as const;
export type SocialPostCarouselContinuityReferenceRole =
  (typeof SOCIAL_POST_CAROUSEL_CONTINUITY_REFERENCE_ROLES)[number];
export const socialPostCarouselContinuityReferenceRoleSchema = z.enum(
  SOCIAL_POST_CAROUSEL_CONTINUITY_REFERENCE_ROLES,
);

export const BRAND_VISUAL_UNIVERSE_IMAGE_SECTIONS = [
  "photography_archetype",
  "environment",
  "material",
  "motif",
] as const;
export type BrandVisualUniverseImageSection = (typeof BRAND_VISUAL_UNIVERSE_IMAGE_SECTIONS)[number];
export const brandVisualUniverseImageSectionSchema = z.enum(BRAND_VISUAL_UNIVERSE_IMAGE_SECTIONS);

export const SOCIAL_POST_STATUSES = [
  "draft",
  "ready",
  "approved",
  "scheduled",
  "published",
  "failed",
] as const;
export type SocialPostStatus = (typeof SOCIAL_POST_STATUSES)[number];
export const socialPostStatusSchema = z.enum(SOCIAL_POST_STATUSES);

export const SOCIAL_ACCOUNT_PLATFORMS = ["instagram"] as const;
export type SocialAccountPlatform = (typeof SOCIAL_ACCOUNT_PLATFORMS)[number];
export const socialAccountPlatformSchema = z.enum(SOCIAL_ACCOUNT_PLATFORMS);

export const SOCIAL_ACCOUNT_CONNECTION_STATUSES = [
  "connected",
  "expired",
  "revoked",
  "error",
] as const;
export type SocialAccountConnectionStatus = (typeof SOCIAL_ACCOUNT_CONNECTION_STATUSES)[number];
export const socialAccountConnectionStatusSchema = z.enum(SOCIAL_ACCOUNT_CONNECTION_STATUSES);

export const SOCIAL_ACCOUNT_CONNECTION_AUTH_FLOWS = ["direct", "meta"] as const;
export type SocialAccountConnectionAuthFlow = (typeof SOCIAL_ACCOUNT_CONNECTION_AUTH_FLOWS)[number];
export const socialAccountConnectionAuthFlowSchema = z.enum(SOCIAL_ACCOUNT_CONNECTION_AUTH_FLOWS);

export const SOCIAL_POST_PUBLICATION_STATUSES = [
  "draft",
  "ready_for_publish",
  "publishing",
  "published",
  "publish_failed",
] as const;
export type SocialPostPublicationStatus = (typeof SOCIAL_POST_PUBLICATION_STATUSES)[number];
export const socialPostPublicationStatusSchema = z.enum(SOCIAL_POST_PUBLICATION_STATUSES);

export const INSTAGRAM_MEDIA_SYNC_STATUSES = ["queued", "processing", "done", "failed"] as const;
export type InstagramMediaSyncStatus = (typeof INSTAGRAM_MEDIA_SYNC_STATUSES)[number];
export const instagramMediaSyncStatusSchema = z.enum(INSTAGRAM_MEDIA_SYNC_STATUSES);

export const INSTAGRAM_MEDIA_TYPES = ["IMAGE", "VIDEO", "CAROUSEL_ALBUM"] as const;
export type InstagramMediaType = (typeof INSTAGRAM_MEDIA_TYPES)[number];
export const instagramMediaTypeSchema = z.enum(INSTAGRAM_MEDIA_TYPES);

export const instagramPublishCarouselSlideSchema = z.object({
  slideId: z.string().trim().min(1).max(80),
  slideIndex: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
});
export type InstagramPublishCarouselSlide = z.infer<typeof instagramPublishCarouselSlideSchema>;

export const instagramPublishPackageSchema = z
  .object({
    platform: z.literal("instagram"),
    postType: instagramSocialPostTypeSchema,
    assetIds: z.array(z.uuid()),
    caption: z.string().trim().min(1).nullable(),
    hashtags: z.array(z.string().trim().min(1)).default([]),
    altText: z.string().trim().min(1).nullable(),
    carouselSlides: z
      .array(instagramPublishCarouselSlideSchema)
      .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES)
      .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES)
      .optional(),
  })
  .superRefine((value, context) => {
    const isInstagramCarousel =
      value.postType === "carousel_portrait" || value.postType === "carousel_square";
    if (!isInstagramCarousel && value.assetIds.length === 0) {
      context.addIssue({
        code: "custom",
        path: ["assetIds"],
        message: "At least one generated asset is required for Instagram feed publishing.",
      });
    }

    if (isInstagramCarousel) {
      const slideCount = value.carouselSlides?.length ?? 0;
      if (
        slideCount < SOCIAL_POST_CAROUSEL_MIN_SLIDES ||
        slideCount > SOCIAL_POST_CAROUSEL_MAX_SLIDES
      ) {
        context.addIssue({
          code: "custom",
          path: ["carouselSlides"],
          message: `Instagram carousel publishing requires ${SOCIAL_POST_CAROUSEL_MIN_SLIDES}-${SOCIAL_POST_CAROUSEL_MAX_SLIDES} rendered slides.`,
        });
      }
      const slideIndexes = new Set((value.carouselSlides ?? []).map((slide) => slide.slideIndex));
      if (slideIndexes.size !== slideCount) {
        context.addIssue({
          code: "custom",
          path: ["carouselSlides"],
          message: "Instagram carousel publishing requires unique slide indexes.",
        });
      }
    }
  });
export type InstagramPublishPackage = z.infer<typeof instagramPublishPackageSchema>;

export const socialPostGeneratedMetadataV1Schema = z.object({
  caption: z.string().trim().min(1),
  hashtags: z.array(z.string().trim().min(1)).default([]),
  altText: z.string().trim().min(1),
});
export type SocialPostGeneratedMetadataV1 = z.infer<typeof socialPostGeneratedMetadataV1Schema>;

export const socialPostCopyTraceSourceTypeSchema = z.enum([
  "request",
  "campaign_slot",
  "campaign_brief",
  "audience_profile",
  "audience_fact",
  "brand_fact",
  "emotional_context",
  "visual_context",
  "template",
  "carousel_structure",
  "standalone_batch",
  "constraint",
  "planner_output",
]);
export type SocialPostCopyTraceSourceType = z.infer<typeof socialPostCopyTraceSourceTypeSchema>;

export const socialPostCopyTraceCitationV1Schema = z.object({
  sourceType: socialPostCopyTraceSourceTypeSchema,
  sourcePath: z.string().trim().min(1).max(240),
  sourceId: z.string().trim().max(160),
  label: z.string().trim().min(1).max(160),
  valueExcerpt: z.string().trim().min(1).max(700),
  relevance: z.string().trim().max(500),
});
export type SocialPostCopyTraceCitationV1 = z.infer<typeof socialPostCopyTraceCitationV1Schema>;

export const socialPostCopyTraceDecisionV1Schema = z.object({
  target: z.string().trim().min(1).max(240),
  selectedText: z.string().trim().min(1).max(2200),
  rationale: z.string().trim().min(1).max(1200),
  citations: z.array(socialPostCopyTraceCitationV1Schema).min(1).max(8),
  alternativesRejected: z.array(z.string().trim().min(1).max(500)).max(5),
  confidence: z.number().finite().min(0).max(1),
});
export type SocialPostCopyTraceDecisionV1 = z.infer<typeof socialPostCopyTraceDecisionV1Schema>;

export const socialPostCopyTraceRejectedAlternativeV2Schema = z.object({
  text: z.string().trim().min(1).max(500),
  reason: z.string().trim().min(1).max(800),
});
export type SocialPostCopyTraceRejectedAlternativeV2 = z.infer<
  typeof socialPostCopyTraceRejectedAlternativeV2Schema
>;

export const socialPostCopyTraceDecisionV2Schema = z.object({
  target: z.string().trim().min(1).max(240),
  selectedText: z.string().trim().min(1).max(2200),
  rationale: z.string().trim().min(1).max(1200),
  citations: z.array(socialPostCopyTraceCitationV1Schema).min(1).max(8),
  rejectedAlternatives: z.array(socialPostCopyTraceRejectedAlternativeV2Schema).max(5),
  confidence: z.number().finite().min(0).max(1),
});
export type SocialPostCopyTraceDecisionV2 = z.infer<typeof socialPostCopyTraceDecisionV2Schema>;

export const socialPostCopyTraceV1Schema = z.object({
  type: z.literal("social_post.copy_trace"),
  version: z.literal(1),
  generator: z.enum(["publish_metadata_copywriter", "visual_planner", "carousel_planner"]),
  decisions: z.array(socialPostCopyTraceDecisionV1Schema).min(1).max(80),
  warnings: z.array(z.string().trim().min(1).max(500)),
});
export type SocialPostCopyTraceV1 = z.infer<typeof socialPostCopyTraceV1Schema>;

export const socialPostCopyTraceV2Schema = z.object({
  type: z.literal("social_post.copy_trace"),
  version: z.literal(2),
  generator: z.enum(["publish_metadata_copywriter", "visual_planner", "carousel_planner"]),
  decisions: z.array(socialPostCopyTraceDecisionV2Schema).min(1).max(80),
  warnings: z.array(z.string().trim().min(1).max(500)),
});
export type SocialPostCopyTraceV2 = z.infer<typeof socialPostCopyTraceV2Schema>;

const legacySocialPostAspectRatioSchema = z.enum(["1:1", "4:5", "9:16"]);

export const socialPostGenerationTaskRequestV1Schema = z.object({
  type: z.literal("social_post.request"),
  version: z.literal(1),
  prompt: z.string().trim().min(1).optional(),
  platform: socialPostPlatformSchema.optional(),
  aspectRatio: legacySocialPostAspectRatioSchema.optional(),
  variantCount: z.number().int().positive().max(10).default(1),
});
export type SocialPostGenerationTaskRequestV1 = z.infer<
  typeof socialPostGenerationTaskRequestV1Schema
>;

const addUnsupportedPostTypeForPlatformIssue = (
  value: {
    platform: z.infer<typeof socialPostPlatformSchema>;
    postType: z.infer<typeof socialPostTypeSchema>;
  },
  ctx: z.RefinementCtx,
): void => {
  if (!isSocialPostTypeSupportedByPlatform(value.platform, value.postType)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["postType"],
      message: "Unsupported post type for platform.",
    });
  }
};

export const socialPostGenerationTaskRequestV2Schema = z
  .object({
    type: z.literal("social_post.request"),
    version: z.literal(2),
    prompt: z.string().trim().min(1),
    platform: socialPostPlatformSchema,
    postType: socialPostTypeSchema,
    variantCount: z.number().int().positive().max(10).default(1),
    brandPresence: standaloneAssetBrandPresenceSchema.default("branded"),
    assetCreationFlow: assetCreationFlowSchema.optional(),
    layoutTemplateId: socialPostLayoutTemplateIdSchema.optional(),
    carouselFormatKey: socialPostCarouselFormatKeySchema.optional(),
    audienceProfile: audienceProfileSnapshotV1Schema.optional(),
    effectiveAudience: effectiveAudienceContextV1Schema.optional(),
  })
  .superRefine((value, ctx) => {
    const isCarousel = isCarouselSocialPostType(value.postType);
    addUnsupportedPostTypeForPlatformIssue(value, ctx);

    if (isCarousel && value.assetCreationFlow) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetCreationFlow"],
        message: "Asset creation flow overrides only support single-post requests.",
      });
    }

    if (isCarousel && value.layoutTemplateId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["layoutTemplateId"],
        message: "Layout template overrides only support single-post requests.",
      });
    }

    if (!isCarousel && value.carouselFormatKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["carouselFormatKey"],
        message: "Carousel format overrides only support carousel requests.",
      });
    }

    if (value.assetCreationFlow === "freestyle" && value.layoutTemplateId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["layoutTemplateId"],
        message: "Freestyle social post generation cannot use a layout template override.",
      });
    }
  });
export type SocialPostGenerationTaskRequestV2 = z.infer<
  typeof socialPostGenerationTaskRequestV2Schema
>;

const socialPostSourceBriefSiblingV1Schema = z.object({
  index: z.number().int().nonnegative(),
  concept: z.string().trim().min(1),
  objective: z.string().trim().min(1),
  visualPerspectiveRole: z.string().trim().min(1).nullable(),
});

export type SocialPostSourceBriefV1 =
  | { version: 1; origin: "direct"; direction: string }
  | {
      version: 1;
      origin: "campaign";
      campaignId?: string;
      campaignName: string;
      campaignBrief: CampaignBriefV1;
      slot: CampaignContentSlotV1;
      additionalDirection: string | null;
    }
  | {
      version: 1;
      origin: "standalone_batch";
      batchId?: string;
      batchName: string;
      batchDirection: string;
      slot: CampaignContentSlotV1;
      siblings: Array<{
        index: number;
        concept: string;
        objective: string;
        visualPerspectiveRole: string | null;
      }>;
    };

export const socialPostSourceBriefV1Schema: z.ZodType<SocialPostSourceBriefV1> =
  z.discriminatedUnion("origin", [
    z.object({
      version: z.literal(1),
      origin: z.literal("direct"),
      direction: z.string().trim().min(1),
    }),
    z.object({
      version: z.literal(1),
      origin: z.literal("campaign"),
      campaignId: z.uuid().optional(),
      campaignName: z.string().trim().min(1),
      campaignBrief: campaignBriefV1Schema,
      slot: campaignContentSlotV1Schema,
      additionalDirection: z.string().trim().min(1).nullable().default(null),
    }),
    z.object({
      version: z.literal(1),
      origin: z.literal("standalone_batch"),
      batchId: z.uuid().optional(),
      batchName: z.string().trim().min(1),
      batchDirection: z.string().trim().min(1),
      slot: campaignContentSlotV1Schema,
      siblings: z.array(socialPostSourceBriefSiblingV1Schema).default([]),
    }),
  ]) as z.ZodType<SocialPostSourceBriefV1>;

export type SocialPostGenerationTaskRequestV3 = Omit<
  SocialPostGenerationTaskRequestV2,
  "version" | "prompt"
> & {
  version: 3;
  sourceBrief: SocialPostSourceBriefV1;
};

export const socialPostGenerationTaskRequestV3Schema: z.ZodType<SocialPostGenerationTaskRequestV3> =
  z
    .object({
      type: z.literal("social_post.request"),
      version: z.literal(3),
      sourceBrief: socialPostSourceBriefV1Schema,
      platform: socialPostPlatformSchema,
      postType: socialPostTypeSchema,
      variantCount: z.number().int().positive().max(10).default(1),
      brandPresence: standaloneAssetBrandPresenceSchema.default("branded"),
      assetCreationFlow: assetCreationFlowSchema.optional(),
      layoutTemplateId: socialPostLayoutTemplateIdSchema.optional(),
      carouselFormatKey: socialPostCarouselFormatKeySchema.optional(),
      audienceProfile: audienceProfileSnapshotV1Schema.optional(),
      effectiveAudience: effectiveAudienceContextV1Schema.optional(),
    })
    .superRefine((value, ctx) => {
      const isCarousel = isCarouselSocialPostType(value.postType);
      addUnsupportedPostTypeForPlatformIssue(value, ctx);
      if (isCarousel && value.assetCreationFlow) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assetCreationFlow"],
          message: "Asset creation flow overrides only support single-post requests.",
        });
      }
      if (isCarousel && value.layoutTemplateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["layoutTemplateId"],
          message: "Layout template overrides only support single-post requests.",
        });
      }
      if (!isCarousel && value.carouselFormatKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["carouselFormatKey"],
          message: "Carousel format overrides only support carousel requests.",
        });
      }
      if (value.assetCreationFlow === "freestyle" && value.layoutTemplateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["layoutTemplateId"],
          message: "Freestyle social post generation cannot use a layout template override.",
        });
      }
    }) as z.ZodType<SocialPostGenerationTaskRequestV3>;

export const socialPostGenerationTaskRequestSchema = z.union([
  socialPostGenerationTaskRequestV1Schema,
  socialPostGenerationTaskRequestV2Schema,
  socialPostGenerationTaskRequestV3Schema,
]);
export type SocialPostGenerationTaskRequest =
  | SocialPostGenerationTaskRequestV1
  | SocialPostGenerationTaskRequestV2
  | SocialPostGenerationTaskRequestV3;

export const ASSISTANT_IMAGE_GENERATION_ASPECT_RATIOS = ["1:1", "4:5", "9:16", "16:9"] as const;
export type AssistantImageGenerationAspectRatio =
  (typeof ASSISTANT_IMAGE_GENERATION_ASPECT_RATIOS)[number];
export const assistantImageGenerationAspectRatioSchema = z.enum(
  ASSISTANT_IMAGE_GENERATION_ASPECT_RATIOS,
);

export const assistantImageGenerationTaskRequestV1Schema = z.object({
  type: z.literal("assistant_image_generation.request"),
  version: z.literal(1),
  brandId: z.uuid(),
  chatId: z.uuid(),
  batchId: z.uuid(),
  prompt: z.string().trim().min(1).max(4000),
  variantCount: z.number().int().positive().max(4).default(1),
  aspectRatio: assistantImageGenerationAspectRatioSchema.default("1:1"),
  sourceGeneratedAssetId: z.uuid().optional(),
  audienceProfile: audienceProfileSnapshotV1Schema.optional(),
});
export type AssistantImageGenerationTaskRequestV1 = z.infer<
  typeof assistantImageGenerationTaskRequestV1Schema
>;

export const socialPostCopyGenerationTaskRequestV1Schema = z
  .object({
    type: z.literal("social_post_copy.request"),
    version: z.literal(1),
    campaignId: z.uuid().optional(),
    standaloneAssetBatchId: z.uuid().optional(),
    scheduledPostId: z.uuid(),
    visualGenerationTaskId: z.uuid().optional(),
    prompt: z.string().trim().min(1),
    platform: socialPostPlatformSchema,
    postType: socialPostTypeSchema,
    brandPresence: standaloneAssetBrandPresenceSchema.default("branded"),
    audienceProfile: audienceProfileSnapshotV1Schema.optional(),
    effectiveAudience: effectiveAudienceContextV1Schema.optional(),
  })
  .superRefine((value, ctx) => {
    if (Boolean(value.campaignId) === Boolean(value.standaloneAssetBatchId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["campaignId"],
        message: "Exactly one social post owner is required.",
      });
    }

    addUnsupportedPostTypeForPlatformIssue(value, ctx);
  });
export type SocialPostCopyGenerationTaskRequestV1 = z.infer<
  typeof socialPostCopyGenerationTaskRequestV1Schema
>;

export type SocialPostCopyGenerationTaskRequestV2 = Omit<
  SocialPostCopyGenerationTaskRequestV1,
  "version" | "prompt"
> & {
  version: 2;
  sourceBrief: SocialPostSourceBriefV1;
};

export const socialPostCopyGenerationTaskRequestV2Schema: z.ZodType<SocialPostCopyGenerationTaskRequestV2> =
  z
    .object({
      type: z.literal("social_post_copy.request"),
      version: z.literal(2),
      campaignId: z.uuid().optional(),
      standaloneAssetBatchId: z.uuid().optional(),
      scheduledPostId: z.uuid(),
      visualGenerationTaskId: z.uuid().optional(),
      sourceBrief: socialPostSourceBriefV1Schema,
      platform: socialPostPlatformSchema,
      postType: socialPostTypeSchema,
      brandPresence: standaloneAssetBrandPresenceSchema.default("branded"),
      audienceProfile: audienceProfileSnapshotV1Schema.optional(),
      effectiveAudience: effectiveAudienceContextV1Schema.optional(),
    })
    .superRefine((value, ctx) => {
      if (Boolean(value.campaignId) === Boolean(value.standaloneAssetBatchId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["campaignId"],
          message: "Exactly one social post owner is required.",
        });
      }
      addUnsupportedPostTypeForPlatformIssue(value, ctx);
    }) as z.ZodType<SocialPostCopyGenerationTaskRequestV2>;

export const socialPostCopyGenerationTaskRequestSchema = z.union([
  socialPostCopyGenerationTaskRequestV1Schema,
  socialPostCopyGenerationTaskRequestV2Schema,
]);
export type SocialPostCopyGenerationTaskRequest = z.infer<
  typeof socialPostCopyGenerationTaskRequestSchema
>;

export const normalizeSocialPostCopyGenerationTaskRequest = (
  input: unknown,
): SocialPostCopyGenerationTaskRequestV2 => {
  const parsed = socialPostCopyGenerationTaskRequestSchema.parse(input);
  if (parsed.version === 2) return parsed;
  const { prompt, version: _version, ...rest } = parsed;
  return {
    ...rest,
    version: 2,
    sourceBrief: { version: 1, origin: "direct", direction: prompt },
  };
};

const socialPostImageRegenerationPromptSchema = z.string().trim().min(1).max(2000).optional();
const socialPostImageRegenerationBrushPointSchema = z.object({
  x: z.number().finite().nonnegative(),
  y: z.number().finite().nonnegative(),
});
const socialPostImageRegenerationBrushStrokeSchema = z.object({
  id: z.string().trim().min(1),
  points: z.array(socialPostImageRegenerationBrushPointSchema).min(2).max(2000),
});

export const SOCIAL_POST_IMAGE_REGENERATION_OVERLAY_COLOR = "#ff00ff" as const;
export const DEFAULT_SOCIAL_POST_IMAGE_REGENERATION_BRUSH_RADIUS = 28;

const socialPostImageRegenerationTaskRequestBaseV1Schema = z
  .object({
    type: z.literal("social_post_image_regeneration.request"),
    version: z.literal(1),
    campaignId: z.uuid().optional(),
    standaloneAssetBatchId: z.uuid().optional(),
    scheduledPostId: z.uuid(),
    blockId: z.string().trim().min(1),
    sourceGeneratedAssetId: z.uuid(),
    prompt: socialPostImageRegenerationPromptSchema,
    applyAsDraftOverride: z.boolean().default(true),
    audienceProfile: audienceProfileSnapshotV1Schema.optional(),
  })
  .refine((data) => Boolean(data.campaignId) !== Boolean(data.standaloneAssetBatchId), {
    message: "Exactly one social post owner is required.",
    path: ["campaignId"],
  });

export const socialPostImageRegenerationTaskRequestFullV1Schema =
  socialPostImageRegenerationTaskRequestBaseV1Schema.extend({
    mode: z.literal("full"),
  });

export const socialPostImageRegenerationTaskRequestPartialV1Schema =
  socialPostImageRegenerationTaskRequestBaseV1Schema.extend({
    mode: z.literal("partial"),
    brushRadius: z
      .number()
      .int()
      .positive()
      .max(256)
      .default(DEFAULT_SOCIAL_POST_IMAGE_REGENERATION_BRUSH_RADIUS),
    strokes: z.array(socialPostImageRegenerationBrushStrokeSchema).min(1).max(64),
  });

export const socialPostImageRegenerationTaskRequestV1Schema = z.discriminatedUnion("mode", [
  socialPostImageRegenerationTaskRequestFullV1Schema,
  socialPostImageRegenerationTaskRequestPartialV1Schema,
]);
export type SocialPostImageRegenerationTaskRequestV1 = z.infer<
  typeof socialPostImageRegenerationTaskRequestV1Schema
>;

export const DEFAULT_SOCIAL_POST_GENERATION_PROMPT = "Create a brand-aligned social post concept.";

const LEGACY_POST_TYPE_BY_ASPECT_RATIO = {
  "1:1": "feed_square",
  "4:5": "feed_portrait",
  "9:16": "story",
} as const;

export const normalizeSocialPostGenerationTaskRequest = (
  input: unknown,
): SocialPostGenerationTaskRequestV3 => {
  const parsed = socialPostGenerationTaskRequestSchema.parse(input);
  if (parsed.version === 3) {
    return parsed;
  }
  if (parsed.version === 2) {
    const { prompt, version: _version, ...rest } = parsed;
    return {
      ...rest,
      version: 3,
      sourceBrief: { version: 1, origin: "direct", direction: prompt },
    };
  }
  const platform = parsed.platform ?? "instagram";
  const postType =
    platform === "tiktok"
      ? "tiktok_portrait"
      : ((parsed.aspectRatio && LEGACY_POST_TYPE_BY_ASPECT_RATIO[parsed.aspectRatio]) ??
        "feed_portrait");

  return {
    type: "social_post.request",
    version: 3,
    sourceBrief: {
      version: 1,
      origin: "direct",
      direction: parsed.prompt?.trim() || DEFAULT_SOCIAL_POST_GENERATION_PROMPT,
    },
    platform,
    postType,
    variantCount: parsed.variantCount,
    brandPresence: "branded",
  };
};

export const socialPostVisualPlanSummaryV1Schema = z.object({
  type: z.literal("social_post.visual_plan_summary"),
  version: z.literal(1),
  summary: z.string().trim().min(1),
  plannerMode: z.string().trim().min(1).max(80),
  recipeId: socialPostLayoutRecipeIdSchema.nullable(),
  recipeLabel: z.string().trim().min(1).max(120).nullable(),
  visualTone: socialPostRecipeVisualToneSchema.nullable(),
  styleOverrideFields: z.array(z.string().trim().min(1).max(80)).max(16).default([]),
  recipeSpecificFields: z.array(z.string().trim().min(1).max(80)).max(32).default([]),
  templateId: socialPostLayoutTemplateIdSchema.nullable().default(null),
  templateName: socialPostLayoutTemplateNameSchema.nullable().default(null),
  templateTextSlotIds: z.array(z.string().trim().min(1).max(80)).max(32).default([]),
  templateImageSlotIds: z.array(z.string().trim().min(1).max(80)).max(32).default([]),
  templatePatchFields: z.array(z.string().trim().min(1).max(80)).max(32).default([]),
  templateSafeZoneHintCount: z.number().int().nonnegative().default(0),
});
export type SocialPostVisualPlanSummaryV1 = z.infer<typeof socialPostVisualPlanSummaryV1Schema>;

const socialPostSafeZoneRectV1Schema = z.object({
  x: z.number().finite().nonnegative(),
  y: z.number().finite().nonnegative(),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
});

export const socialPostImageSafeZoneHintV1Schema = z.object({
  blockId: z.string().trim().min(1).max(120),
  imageSlotId: z.string().trim().min(1).max(80).optional(),
  avoidOverlayZone: socialPostSafeZoneRectV1Schema,
  reason: z.string().trim().min(1).max(500),
});
export type SocialPostImageSafeZoneHintV1 = z.infer<typeof socialPostImageSafeZoneHintV1Schema>;

export const socialPostCarouselSlideScenePlanV1Schema = z.object({
  type: z.literal("social_post.carousel_slide_scene_plan"),
  version: z.literal(1),
  slideId: z.string().trim().min(1).max(80),
  slideIndex: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  slideRole: socialPostCarouselSlideRoleSchema,
  templateKey: z.string().trim().min(1).max(80),
  plan: socialPostScenePlanV1Schema,
});
export type SocialPostCarouselSlideScenePlanV1 = z.infer<
  typeof socialPostCarouselSlideScenePlanV1Schema
>;

export const socialPostImageCandidateSelectionStatusSchema = z.enum([
  "qa_passed",
  "qa_failed_selected_best",
  "qa_unavailable_selected_best",
  "qa_disabled",
]);
export type SocialPostImageCandidateSelectionStatus = z.infer<
  typeof socialPostImageCandidateSelectionStatusSchema
>;

export type SocialPostImageQaFailurePolicy = z.infer<typeof socialPostImageQaFailurePolicySchema>;

export const socialPostImageCandidateQaFailureSchema = z.enum([
  "brand_mismatch",
  "prompt_mismatch",
  "story_mismatch",
  "composition_issue",
  "negative_space_issue",
  "text_or_logo_violation",
  "people_policy_violation",
  "artifact_risk",
]);
export type SocialPostImageCandidateQaFailure = z.infer<
  typeof socialPostImageCandidateQaFailureSchema
>;

export const socialPostImageCandidateQaResultV1Schema = z.object({
  type: z.literal("social_post.image_candidate_qa_result"),
  version: z.literal(1),
  passed: z.boolean(),
  overallScorePct: z.number().int().min(0).max(100),
  scores: z.object({
    brandFitPct: z.number().int().min(0).max(100),
    promptAdherencePct: z.number().int().min(0).max(100),
    storyMatchPct: z.number().int().min(0).max(100).default(100),
    compositionPct: z.number().int().min(0).max(100),
    negativeSpacePct: z.number().int().min(0).max(100),
    artifactRiskPct: z.number().int().min(0).max(100),
    textOrLogoViolationRiskPct: z.number().int().min(0).max(100),
    peoplePolicyRiskPct: z.number().int().min(0).max(100),
  }),
  failures: z.array(socialPostImageCandidateQaFailureSchema).default([]),
  rationale: z.string().trim().min(1).max(2000),
  improvementPrompt: z.string().trim().min(1).max(2000).optional(),
  model: z.string().trim().min(1).optional(),
  provider: z.string().trim().min(1).optional(),
});
export type SocialPostImageCandidateQaResultV1 = z.infer<
  typeof socialPostImageCandidateQaResultV1Schema
>;

export const socialPostImageCandidateMetadataV1Schema = z.object({
  type: z.literal("social_post.image_candidate"),
  version: z.literal(1),
  candidateGroupId: z.uuid(),
  candidateId: z.uuid(),
  candidateIndex: z.number().int().positive(),
  attemptIndex: z.number().int().nonnegative(),
  selected: z.boolean().default(false),
  selectionStatus: socialPostImageCandidateSelectionStatusSchema.optional(),
  selectionReason: z.string().trim().min(1).max(1000).optional(),
  qaResult: socialPostImageCandidateQaResultV1Schema.nullable().optional(),
  failurePolicy: socialPostImageQaFailurePolicySchema.optional(),
});
export type SocialPostImageCandidateMetadataV1 = z.infer<
  typeof socialPostImageCandidateMetadataV1Schema
>;

export const socialPostImageCarouselSlideMetadataV1Schema = z.object({
  id: z.string().trim().min(1).max(80),
  index: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  role: socialPostCarouselSlideRoleSchema,
});
export type SocialPostImageCarouselSlideMetadataV1 = z.infer<
  typeof socialPostImageCarouselSlideMetadataV1Schema
>;

export const socialPostImageCandidateGroupV1Schema = z.object({
  type: z.literal("social_post.image_candidate_group"),
  version: z.literal(1),
  candidateGroupId: z.uuid(),
  blockId: z.string().trim().min(1),
  carouselSlide: socialPostImageCarouselSlideMetadataV1Schema.optional(),
  selectedCandidateId: z.uuid(),
  selectedGeneratedAssetId: z.uuid().optional(),
  candidateGeneratedAssetIds: z.array(z.uuid()).default([]),
  selectionStatus: socialPostImageCandidateSelectionStatusSchema,
  failurePolicy: socialPostImageQaFailurePolicySchema.optional(),
  candidateCount: z.number().int().positive(),
  qaEnabled: z.boolean(),
  retryEnabled: z.boolean().default(false),
  retryCount: z.number().int().nonnegative().default(0),
});
export type SocialPostImageCandidateGroupV1 = z.infer<typeof socialPostImageCandidateGroupV1Schema>;

export const socialPostGenerationTaskResultV1Schema = z.object({
  type: z.literal("social_post.result"),
  version: z.literal(1),
  resolvedBrandContextId: z.uuid().optional(),
  resolvedBrandContextVersion: z.union([z.literal(1), z.literal(2)]).optional(),
  plan: socialPostScenePlanV1Schema.optional(),
  carouselPlan: socialPostCarouselPlanV1Schema.optional(),
  carouselSlidePlans: z.array(socialPostCarouselSlideScenePlanV1Schema).optional(),
  metadata: socialPostGeneratedMetadataV1Schema.optional(),
  copyTrace: socialPostCopyTraceV2Schema.optional(),
  visualPlanSummary: socialPostVisualPlanSummaryV1Schema.optional(),
  imageSafeZoneHints: z.array(socialPostImageSafeZoneHintV1Schema).optional(),
  imageCandidateGroups: z.array(socialPostImageCandidateGroupV1Schema).optional(),
  socialPostId: z.uuid().optional(),
  generatedAssetIds: z.array(z.uuid()).default([]),
  warnings: z.array(z.string().min(1)).default([]),
  summary: z.string().min(1).optional(),
});
export type SocialPostGenerationTaskResultV1 = z.infer<
  typeof socialPostGenerationTaskResultV1Schema
>;

export const socialPostCopyGenerationTaskResultV1Schema = z
  .object({
    type: z.literal("social_post_copy.result"),
    version: z.literal(1),
    resolvedBrandContextId: z.uuid().optional(),
    resolvedBrandContextVersion: z.union([z.literal(1), z.literal(2)]).optional(),
    campaignId: z.uuid().optional(),
    standaloneAssetBatchId: z.uuid().optional(),
    scheduledPostId: z.uuid(),
    metadata: socialPostGeneratedMetadataV1Schema,
    copyTrace: socialPostCopyTraceV2Schema.optional(),
    warnings: z.array(z.string().min(1)).default([]),
    summary: z.string().min(1).optional(),
  })
  .refine((data) => Boolean(data.campaignId) !== Boolean(data.standaloneAssetBatchId), {
    message: "Exactly one social post owner is required.",
    path: ["campaignId"],
  });
export type SocialPostCopyGenerationTaskResultV1 = z.infer<
  typeof socialPostCopyGenerationTaskResultV1Schema
>;

type SocialPostGenerationTaskResultParseResult =
  | { success: true; data: SocialPostGenerationTaskResultV1 }
  | { success: false; error: z.ZodError };

type SocialPostCopyGenerationTaskResultParseResult =
  | { success: true; data: SocialPostCopyGenerationTaskResultV1 }
  | { success: false; error: z.ZodError };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const safeParseSocialPostGenerationTaskResult = (
  input: unknown,
): SocialPostGenerationTaskResultParseResult => {
  const parsed = socialPostGenerationTaskResultV1Schema.safeParse(input);
  if (parsed.success) {
    return parsed;
  }

  if (!isRecord(input) || !("copyTrace" in input)) {
    return parsed;
  }

  const inputWithoutCopyTrace = { ...input };
  delete inputWithoutCopyTrace.copyTrace;
  const parsedWithoutCopyTrace =
    socialPostGenerationTaskResultV1Schema.safeParse(inputWithoutCopyTrace);
  return parsedWithoutCopyTrace.success ? parsedWithoutCopyTrace : parsed;
};

export const normalizeSocialPostGenerationTaskResult = (
  input: unknown,
): SocialPostGenerationTaskResultV1 => {
  const parsed = safeParseSocialPostGenerationTaskResult(input);
  if (parsed.success) {
    return parsed.data;
  }

  throw parsed.error;
};

export const safeParseSocialPostCopyGenerationTaskResult = (
  input: unknown,
): SocialPostCopyGenerationTaskResultParseResult => {
  const parsed = socialPostCopyGenerationTaskResultV1Schema.safeParse(input);
  if (parsed.success) {
    return parsed;
  }

  if (!isRecord(input) || !("copyTrace" in input)) {
    return parsed;
  }

  const inputWithoutCopyTrace = { ...input };
  delete inputWithoutCopyTrace.copyTrace;
  const parsedWithoutCopyTrace =
    socialPostCopyGenerationTaskResultV1Schema.safeParse(inputWithoutCopyTrace);
  return parsedWithoutCopyTrace.success ? parsedWithoutCopyTrace : parsed;
};

export const normalizeSocialPostCopyGenerationTaskResult = (
  input: unknown,
): SocialPostCopyGenerationTaskResultV1 => {
  const parsed = safeParseSocialPostCopyGenerationTaskResult(input);
  if (parsed.success) {
    return parsed.data;
  }

  throw parsed.error;
};

export const socialPostImageRegenerationTaskResultV1Schema = z
  .object({
    type: z.literal("social_post_image_regeneration.result"),
    version: z.literal(1),
    mode: z.enum(["full", "partial"]),
    campaignId: z.uuid().optional(),
    standaloneAssetBatchId: z.uuid().optional(),
    scheduledPostId: z.uuid(),
    blockId: z.string().trim().min(1),
    sourceGeneratedAssetId: z.uuid(),
    generatedAssetId: z.uuid(),
    resolvedBrandContextId: z.uuid().optional(),
    resolvedBrandContextVersion: z.union([z.literal(1), z.literal(2)]).optional(),
    prompt: z.string().trim().min(1).optional(),
    qaEnabled: z.boolean().optional(),
    qaResult: socialPostImageCandidateQaResultV1Schema.nullable().optional(),
    selectionStatus: socialPostImageCandidateSelectionStatusSchema.optional(),
    failurePolicy: socialPostImageQaFailurePolicySchema.default("select_best"),
    retryEnabled: z.boolean().default(false),
    retryCount: z.number().int().nonnegative().default(0),
    warnings: z.array(z.string().min(1)).default([]),
  })
  .refine((data) => Boolean(data.campaignId) !== Boolean(data.standaloneAssetBatchId), {
    message: "Exactly one social post owner is required.",
    path: ["campaignId"],
  });
export type SocialPostImageRegenerationTaskResultV1 = z.infer<
  typeof socialPostImageRegenerationTaskResultV1Schema
>;

export const assistantImageGenerationTaskResultV1Schema = z.object({
  type: z.literal("assistant_image_generation.result"),
  version: z.literal(1),
  batchId: z.uuid(),
  generatedAssetIds: z.array(z.uuid()).default([]),
  warnings: z.array(z.string().min(1)).default([]),
  summary: z.string().min(1).optional(),
});
export type AssistantImageGenerationTaskResultV1 = z.infer<
  typeof assistantImageGenerationTaskResultV1Schema
>;

export const generatedImageAssetPayloadV1Schema = z
  .object({
    type: z.literal("social_post.image"),
    version: z.literal(1),
    assetVariant: z.enum(["master", "derivative"]).optional(),
    generationTaskId: z.uuid(),
    blockId: z.string().trim().min(1),
    prompt: z.string().trim().min(1),
    plannerImageBrief: z.string().trim().min(1).optional(),
    realizerPrompt: z.string().trim().min(1).optional(),
    requestPrompt: z.string().trim().min(1).optional(),
    imageSafeZoneHints: z.array(socialPostImageSafeZoneHintV1Schema).optional(),
    imageCandidate: socialPostImageCandidateMetadataV1Schema.optional(),
    backgroundMode: socialPostImageBackgroundModeSchema.optional(),
    referenceImageCount: z.number().int().nonnegative().optional(),
    referenceImages: z
      .array(
        z.object({
          canonicalAssetId: z.uuid(),
          sourceUrl: z.url(),
          objectKey: z.string().trim().min(1),
          width: z.number().int().positive().nullable().optional(),
          height: z.number().int().positive().nullable().optional(),
          containsRealPeople: z.boolean().nullable().optional(),
          containsRealPeopleConfidencePct: z.number().int().min(0).max(100).nullable().optional(),
          realPeopleReferencePolicy: z
            .enum(BRAND_REAL_PEOPLE_REFERENCE_POLICIES)
            .default("style_and_setting_only"),
        }),
      )
      .default([]),
    model: z.string().trim().min(1),
    provider: socialPostImageRealizerProviderSchema.optional(),
    masterImage: z
      .object({
        objectKey: z.string().trim().min(1),
        mimeType: z.string().trim().min(1),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        sizeBytes: z.number().int().nonnegative().nullable().optional(),
        checksum: z.string().trim().min(1).nullable().optional(),
      })
      .optional(),
    platform: socialPostPlatformSchema,
    postType: socialPostTypeSchema,
    role: z.literal("scene_image").default("scene_image"),
    carouselSlide: socialPostImageCarouselSlideMetadataV1Schema.optional(),
    templateKey: z.string().trim().min(1).optional(),
    regeneration: z
      .object({
        mode: z.enum(["full", "partial"]),
        sourceGeneratedAssetId: z.uuid(),
        prompt: z.string().trim().min(1).optional(),
        brushRadius: z.number().int().positive().optional(),
        strokeCount: z.number().int().nonnegative().optional(),
        overlayColor: z.string().trim().min(1).optional(),
        attemptIndex: z.number().int().nonnegative().optional(),
        selected: z.boolean().optional(),
        selectionStatus: socialPostImageCandidateSelectionStatusSchema.optional(),
        selectionReason: z.string().trim().min(1).max(1000).optional(),
        qaResult: socialPostImageCandidateQaResultV1Schema.nullable().optional(),
        failurePolicy: socialPostImageQaFailurePolicySchema.optional(),
      })
      .optional(),
  })
  .superRefine(addUnsupportedPostTypeForPlatformIssue);
export type GeneratedImageAssetPayloadV1 = z.infer<typeof generatedImageAssetPayloadV1Schema>;

export const brandVisualUniverseImageAssetPayloadV1Schema = z.object({
  type: z.literal("brand_visual_universe.image"),
  version: z.literal(1),
  section: brandVisualUniverseImageSectionSchema,
  itemName: z.string().trim().min(1),
  itemIndex: z.number().int().nonnegative(),
  prompt: z.string().trim().min(1),
  sourceFactPaths: z.array(z.string().trim().min(1)).default([]),
  model: z.string().trim().min(1),
  provider: socialPostImageRealizerProviderSchema.optional(),
});
export type BrandVisualUniverseImageAssetPayloadV1 = z.infer<
  typeof brandVisualUniverseImageAssetPayloadV1Schema
>;

export const assistantChatImageAssetPayloadV1Schema = z.object({
  type: z.literal("assistant_chat.image"),
  version: z.literal(1),
  chatId: z.uuid(),
  batchId: z.uuid(),
  prompt: z.string().trim().min(1),
  aspectRatio: assistantImageGenerationAspectRatioSchema,
  variantIndex: z.number().int().positive(),
  sourceGeneratedAssetId: z.uuid().optional(),
  model: z.string().trim().min(1),
  provider: socialPostImageRealizerProviderSchema.optional(),
});
export type AssistantChatImageAssetPayloadV1 = z.infer<
  typeof assistantChatImageAssetPayloadV1Schema
>;

export const socialPostCarouselContinuityReferenceAssetPayloadV1Schema = z
  .object({
    type: z.literal("social_post.carousel_continuity_reference"),
    version: z.literal(1),
    generationTaskId: z.uuid(),
    role: socialPostCarouselContinuityReferenceRoleSchema,
    visualContinuityMode: z.enum(SOCIAL_POST_CAROUSEL_VISUAL_CONTINUITY_MODES),
    carouselFormatKey: socialPostCarouselFormatKeySchema,
    prompt: z.string().trim().min(1),
    recurringCastLabels: z.array(z.string().trim().min(1).max(60)).max(4).default([]),
    recurringSettingOrObject: z.string().trim().min(1).max(300).nullable().default(null),
    model: z.string().trim().min(1),
    provider: socialPostImageRealizerProviderSchema.optional(),
    platform: socialPostPlatformSchema,
    postType: socialPostTypeSchema,
  })
  .superRefine(addUnsupportedPostTypeForPlatformIssue);
export type SocialPostCarouselContinuityReferenceAssetPayloadV1 = z.infer<
  typeof socialPostCarouselContinuityReferenceAssetPayloadV1Schema
>;

export const generatedAssetPayloadJsonSchema = z.discriminatedUnion("type", [
  generatedImageAssetPayloadV1Schema,
  brandVisualUniverseImageAssetPayloadV1Schema,
  assistantChatImageAssetPayloadV1Schema,
  socialPostCarouselContinuityReferenceAssetPayloadV1Schema,
]);

export const socialPostPayloadV1Schema = z.object({
  type: z.literal("social_post"),
  version: z.literal(1),
  hashtags: z.array(z.string().min(1)).default([]),
  templateKey: z.string().trim().min(1).optional(),
});
export type SocialPostPayloadV1 = z.infer<typeof socialPostPayloadV1Schema>;

export type GenerationTaskRequestJson =
  | SocialPostGenerationTaskRequest
  | SocialPostCopyGenerationTaskRequest
  | SocialPostImageRegenerationTaskRequestV1
  | AssistantImageGenerationTaskRequestV1;
export type GenerationTaskResultJson =
  | SocialPostGenerationTaskResultV1
  | SocialPostCopyGenerationTaskResultV1
  | SocialPostImageRegenerationTaskResultV1
  | AssistantImageGenerationTaskResultV1;
export type GeneratedAssetPayloadJson = z.infer<typeof generatedAssetPayloadJsonSchema>;
export type SocialPostPayloadJson = SocialPostPayloadV1;
